// src/lib/pdfRangeTransport.js
// Opens a PDF from an authenticated endpoint with Range requests only.
// pdf.js's own URL loading starts with a full GET and trusts its
// Content-Length; on the API server that response arrives compressed, so the
// length is wrong and the PDF fails to open. Here the real size comes from a
// 1-byte Range probe (Content-Range), and pdf.js asks for the byte ranges it
// needs for the page on screen.
import { pdfjs } from "react-pdf";

const fetchRange = async (url, headers, begin, endInclusive, attempts = 3) => {
  for (let i = 1; ; i++) {
    try {
      const res = await fetch(url, {
        headers: { ...headers, Range: `bytes=${begin}-${endInclusive}` },
        cache: "no-store",
      });
      if (res.status !== 206 && res.status !== 200) throw new Error(`HTTP ${res.status}`);
      const buf = new Uint8Array(await res.arrayBuffer());
      // A server that ignores Range sends the whole file
      return { res, chunk: res.status === 200 ? buf.subarray(begin, endInclusive + 1) : buf };
    } catch (err) {
      if (i >= attempts) throw err;
      await new Promise((resolve) => setTimeout(resolve, 1000 * i));
    }
  }
};

class AuthRangeTransport extends pdfjs.PDFDataRangeTransport {
  constructor(url, headers, length, initialData) {
    super(length, initialData);
    this.url = url;
    this.headers = headers;
  }

  requestDataRange(begin, end) {
    fetchRange(this.url, this.headers, begin, end - 1)
      .then(({ chunk }) => this.onDataRange(begin, chunk))
      .catch((err) => console.error("PDF range request failed", err));
  }
}

// Resolves to a PDFDataRangeTransport that react-pdf's <Document file> accepts
export const openPdfByRange = async (url, headers) => {
  const { res, chunk } = await fetchRange(url, headers, 0, 0); // 1 byte: just the size
  const match = /\/(\d+)\s*$/.exec(res.headers.get("content-range") || "");
  if (!match) throw new Error("Server did not report the PDF size");
  const length = Number(match[1]);
  return new AuthRangeTransport(url, headers, length, chunk.length === length ? chunk : null);
};
